/**
 * Define user global data fetched from the backend.
 *
 * Copyright © Ircam Amplify 2024-2026
 */

import {
  FIELD_START_DATETIME,
  SORT_ASCENDING,
  SORT_DESCENDING,
} from '@/components/TaskList/JobListDef'
import { logger } from '@/config/logger'
import {
  setAllowedCountries,
  setApiCredentialList,
  setCompanyConsumption,
  setCompanyKpi,
  setInvoiceInfo,
  setJobList,
  setJobListSortDirection,
  setLastJobId,
  setLastWebhookLogId,
  setServicesInfo,
  setUserInfo,
  setWebhookLogs,
  setWebhooks,
  store,
} from '@/store'
import { isDebugMockup } from '@/utils/TestUtil'
import {
  createTimeStampValue,
  DefaultInitialInvoiceListLength,
  isTooOld,
  LogTag,
  singleFlight,
} from '@amplify3/duplo-back-end'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'
import { API } from './API'

/**
 * Batch of API requests to fetch data regarding the consumption of the user
 * (user information, consumption, KPI and invoices).
 *
 * The returned promise is shared through concurrent calls to handle race
 * condition.
 */
export const fetchConsumptionDataBatch = singleFlight(() =>
  Promise.all([
    fetchUser(),
    fetchCompanyConsumption(),
    fetchCompanyKpi(),
    fetchInvoice(),
  ])
)

/**
 * Batch of API requests at initial loading of the webapp.
 *
 * The returned promise is shared through concurrent calls to handle race
 * condition.
 */
export const fetchInitialBatch = singleFlight(() =>
  Promise.all([
    fetchConsumptionDataBatch(),
    fetchApiCredentialList(),
    store.dispatch(setJobListSortDirection(SORT_DESCENDING)),
    fetchJobs({ refreshing: true }),
    fetchServicesInfo(),
    fetchWebhooks(),
    fetchAllowedCountries(),
  ])
)

export const fetchAllowedCountries = async () => {
  const { data } = await axios.create().get('/allowedCountries.json')
  store.dispatch(setAllowedCountries(data ?? []))
}

export const fetchApiCredentialList = async () => {
  const authSession = await fetchAuthSession()
  const { data } = await API(authSession).get('/app')
  store.dispatch(setApiCredentialList(data?.app_clients))
}

export const fetchCompanyConsumption = async () => {
  try {
    if (isTooOld(store.getState().user.companyConsumption)) {
      store.dispatch(setCompanyConsumption(undefined))
      const authSession = await fetchAuthSession()
      const { data } = await API(authSession).get('/company/consumption')
      logger.debug(LogTag.Util, '/company/consumption response:', data)
      store.dispatch(
        setCompanyConsumption(createTimeStampValue(data.consumption))
      )
    }
  } catch (err) {
    console.error('Error in fetchCompanyConsumption:', err)
    throw err
  }
}

export const fetchCompanyKpi = async () => {
  try {
    if (isTooOld(store.getState().user.companyKpi)) {
      const authSession = await fetchAuthSession()
      let data

      if (isDebugMockup()) {
        // load a mock-up of API response for debug and tests
        const mock = await import('@/test/company_kpi_mockup.json')
        data = mock.default
      } else {
        // Request KPI from the backend
        const response = await API(authSession).get('/company/kpis')
        data = response.data
      }

      if (isDebugMockup()) {
        // Warn the use of mock-up in the logs
        logger.warning(
          LogTag.Util,
          'Use test mockup for /company/kpis response:',
          data
        )
      } else {
        logger.debug(LogTag.Util, '/company/kpis response:', data)
      }
      store.dispatch(setCompanyKpi(createTimeStampValue(data?.kpis)))
    }
  } catch (err) {
    console.error('Error in fetchCompanyKpi:', err)
    throw err
  }
}

/**
 * Fetch invoices from the back-end and update the state apiInvoiceResponse.
 * @param previousId The ID of last fetched invoice to get the next ones.
 */
export async function fetchInvoice(previousId: string | null = null) {
  try {
    if (isTooOld(store.getState().user.invoiceInfo)) {
      const params: Record<string, any> = {}
      params['limit'] = DefaultInitialInvoiceListLength
      if (previousId) {
        params['last_key'] = previousId
      }

      API(await fetchAuthSession())
        .get('/user/invoices', { params: params })
        .then((response: any) => {
          store.dispatch(setInvoiceInfo(createTimeStampValue(response?.data)))
        })
    }
  } catch (err) {
    console.error('Error in fetchCompanyKpi:', err)
    throw err
  }
}

export const fetchJobs = async ({ refreshing }: { refreshing: boolean }) => {
  try {
    const { jobList, lastJobId, userInfo, jobListSortDirection } =
      store.getState().user
    const authSession = await fetchAuthSession()
    const sortDir = jobListSortDirection === SORT_ASCENDING ? 'asc' : 'desc'
    const lastJobParam =
      !refreshing && lastJobId?.length ? `&last_job_id=${lastJobId}` : ''

    const url =
      `/company/jobs?client_id=${userInfo?.value.user_cognito_id}` +
      `&limit=10` +
      `&sort=["${FIELD_START_DATETIME}", "${sortDir}"]` +
      lastJobParam +
      `&reserved=with_cost_info`

    const { data } = await API(authSession).get(url)

    const jobsToSave = refreshing
      ? (data?.jobs ?? [])
      : [
          ...new Map(
            [...(jobList?.value ?? []), ...(data?.jobs ?? [])].map((item) => [
              item?.job_id,
              item,
            ])
          ).values(),
        ]
    const sortedJobs =
      jobListSortDirection === SORT_ASCENDING
        ? jobsToSave.sort(
            (a: any, b: any) =>
              new Date(a?.job_start_datetime ?? 0).getTime() -
              new Date(b?.job_start_datetime ?? 0).getTime()
          )
        : jobsToSave.sort(
            (a: any, b: any) =>
              new Date(b?.job_start_datetime ?? 0).getTime() -
              new Date(a?.job_start_datetime ?? 0).getTime()
          )

    store.dispatch(setJobList(createTimeStampValue(sortedJobs)))
    store.dispatch(setLastJobId(data?.last_job_id))
  } catch (err) {
    console.error('Error in fetchJobs:', err)
    throw err
  }
}

export const fetchServicesInfo = async () => {
  try {
    if (isTooOld(store.getState().user.servicesInfo)) {
      const authSession = await fetchAuthSession()
      const { data } = await API(authSession).get('/services-info')
      store.dispatch(setServicesInfo(createTimeStampValue(data)))
    }
  } catch (err) {
    console.error('Error in fetchServicesInfo:', err)
    throw err
  }
}

export const fetchUser = async () => {
  try {
    if (isTooOld(store.getState().user.userInfo)) {
      const authSession = await fetchAuthSession()
      const { data } = await API(authSession).get('/user')
      store.dispatch(setUserInfo(createTimeStampValue(data?.user)))
    }
  } catch (err) {
    console.error('Error in fetchUser:', err)
    throw err
  }
}

export const fetchWebhooks = async () => {
  try {
    const authSession = await fetchAuthSession()
    const { data } = await API(authSession).get('/webhook')
    store.dispatch(setWebhooks(data?.webhooks ?? []))
  } catch (err) {
    console.error('Error in fetchWebhooks:', err)
    throw err
  }
}

export const fetchWebhookLogs = async ({
  webhookId,
  refreshing,
}: {
  webhookId: string
  refreshing: boolean
}) => {
  try {
    const { webhookLogList, webhookLogListSortDirection, lastWebhookLogId } =
      store.getState().user
    const authSession = await fetchAuthSession()

    const { data } = await API(authSession).get(
      `webhook/${webhookId}/log?limit=10&sort=["date", "${webhookLogListSortDirection === SORT_ASCENDING ? 'asc' : 'desc'}"]${!refreshing && lastWebhookLogId !== undefined && lastWebhookLogId?.length > 0 ? `&last_log_id=${lastWebhookLogId}` : ''}`
    )

    const logsToSave = refreshing
      ? (data?.webhook_logs ?? [])
      : [
          ...new Map(
            [...(webhookLogList ?? []), ...(data?.webhook_logs ?? [])].map(
              (item) => [item?.id, item]
            )
          ).values(),
        ]
    const sortedLogs =
      webhookLogListSortDirection === SORT_ASCENDING
        ? logsToSave.sort(
            (a: any, b: any) =>
              new Date(a?.date ?? 0).getTime() -
              new Date(b?.date ?? 0).getTime()
          )
        : logsToSave.sort(
            (a: any, b: any) =>
              new Date(b?.date ?? 0).getTime() -
              new Date(a?.date ?? 0).getTime()
          )

    store.dispatch(setWebhookLogs(sortedLogs))
    store.dispatch(setLastWebhookLogId(data?.last_log_id))
  } catch (err) {
    console.error('Error in fetchWebhookLogs:', err)
    throw err
  }
}
